import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import * as yup from 'yup';
import { apiConfigs } from '../../api/APIConfigs';
import { Church } from '../../api/models/Church';
import { useAsyncRequest } from '../../util/use-async-request';
import { useRequest } from '../../util/use-request';
import { HorizontalContent } from './HorizontalContent';
import { RhButton, RhButtonMode } from './RhButton';
import { RhInput, RhInputMode } from './RhInput';
import { RhModal } from './RhModal';
import { RhSelect, RhSelectOption } from './RhSelect';
import { SizedBox } from './SizedBox';
import { colors, H3, Regular } from './Styles';
import { useUserRoles } from '../../util/use-user-role';
import { Person } from '../../api/models/Person';

export type InvitedUser = {
  email: string;
  role: string;
  churchId?: string;
};

type Props = {
  isOpen: boolean;
  isLoading: boolean;
  onRequestClose: () => void;
  onOK: (users: InvitedUser[]) => void;
};

const createValidationSchema = (isSystemAdmin: boolean) =>
  yup.array().of(
    yup.object().shape({
      email: yup.string().email('Invalid email').required('Email is required'),
      role: yup.string().required('Role is required'),
      churchId: isSystemAdmin ? yup.string().required('Church is required') : yup.string(),
    })
  );

const initInvitedUser: InvitedUser = {
  email: '',
  role: '',
  churchId: undefined,
};

/* -------------------------------------------------------------------------- */
/* START::MainComponent                                                       */
/* -------------------------------------------------------------------------- */

export const InviteModal = ({ isOpen, ...props }: Props) => {
  const { roles: currentUserRoles, loading: isLoadingCurrentUserRoles } = useUserRoles();

  // this is the formstate already
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([{ ...initInvitedUser }]);
  const [formError, setFormError] = useState<string | null>(null);

  const request = useRequest();
  const { loading: loadingRoles, data } = useAsyncRequest(
    useCallback(() => request<string[]>(apiConfigs.getRoles()), [request])
  );

  const { loading: loadingChurches, data: churchesData } = useAsyncRequest(
    useCallback(() => request<Church[]>(apiConfigs.getChurches()), [request])
  );

  useEffect(() => {
    if (!isOpen) {
      setFormError(null);
      setInvitedUsers([{ ...initInvitedUser }]);
    }
  }, [isOpen]);

  const churchOptions: RhSelectOption[] = useMemo(() => {
    if (!churchesData) return [];
    return churchesData.map(church => ({
      value: church.id,
      label: church.name,
    }));
  }, [churchesData]);

  const isSystemAdmin = useMemo(() => {
    if (isLoadingCurrentUserRoles) return false;
    return Array.isArray(currentUserRoles) && currentUserRoles.includes('System Admin');
  }, [currentUserRoles, isLoadingCurrentUserRoles]);

  const roles: string[] | null = useMemo(() => {
    if (!data || isLoadingCurrentUserRoles) {
      return null;
    }

    return isSystemAdmin ? data : data.filter(value => value !== 'System Admin');
  }, [data, isSystemAdmin, isLoadingCurrentUserRoles]);

  return (
    <RhModal contentStyle={{ padding: '22px 22px 20px 27px' }} isOpen={isOpen} onRequestClose={props.onRequestClose}>
      <HorizontalContent
        left={<H3>Invite Someone</H3>}
        right={
          (props.isLoading || loadingRoles || loadingChurches) && <CircleSpinner size={15} color={colors.primary} />
        }
      />

      <InviteForm>
        {invitedUsers.map((email, index) => {
          const isLastElement = invitedUsers.length === index + 1;
          return (
            <div key={index}>
              <EmailInputs>
                <InputDiv>
                  <RhInput
                    disabled={props.isLoading || loadingRoles || loadingChurches}
                    mode={RhInputMode.subtle}
                    placeholder="Email here..."
                    value={invitedUsers[index].email}
                    onChange={e => {
                      setInvitedUsers(f => {
                        let newEmails = [...f];
                        newEmails.splice(index, 1, {
                          ...newEmails[index],
                          email: e.target.value,
                        });
                        return newEmails;
                      });
                    }}
                  />
                </InputDiv>
                {/* Do not allow deletion if there's only one item */}
                {invitedUsers.length > 1 && (
                  <RhButton
                    disabled={props.isLoading || loadingRoles || loadingChurches}
                    mode={RhButtonMode.subtle}
                    prefixIcon={FiTrash2}
                    iconSize={18}
                    background="#fff"
                    onClick={() => {
                      setInvitedUsers(f => {
                        let newEmails = [...f];
                        newEmails.splice(index, 1);
                        return newEmails;
                      });
                    }}
                  />
                )}
                {isLastElement && (
                  <RhButton
                    disabled={props.isLoading || loadingRoles || loadingChurches}
                    mode={RhButtonMode.subtle}
                    prefixIcon={FiPlus}
                    iconSize={18}
                    background="#fff"
                    onClick={() => {
                      setInvitedUsers(f => [...f, { ...initInvitedUser }]);
                    }}
                  />
                )}
              </EmailInputs>
              <SizedBox height={16} />
              <UserButtons>
                {(roles || []).map(role => {
                  const selected = invitedUsers[index].role === role;
                  return (
                    <RhButton
                      key={role}
                      disabled={props.isLoading || loadingRoles || loadingChurches}
                      mode={selected ? RhButtonMode.primary : RhButtonMode.secondary}
                      title={role}
                      onClick={() => {
                        setInvitedUsers(f => {
                          let newEmails = [...f];
                          newEmails.splice(index, 1, {
                            ...newEmails[index],
                            role,
                          });
                          return newEmails;
                        });
                      }}
                    />
                  );
                })}
              </UserButtons>
              {isSystemAdmin && (
                <>
                  <SizedBox height={16} />
                  <ChurchSelectWrapper>
                    <RhSelect
                      value={
                        invitedUsers[index].churchId
                          ? churchOptions.find(opt => opt.value === invitedUsers[index].churchId) || null
                          : null
                      }
                      onChange={(option: RhSelectOption) => {
                        setInvitedUsers(f => {
                          let newEmails = [...f];
                          newEmails.splice(index, 1, {
                            ...newEmails[index],
                            churchId: option.value,
                          });
                          return newEmails;
                        });
                      }}
                      options={churchOptions}
                      placeholder="Select Church"
                      isDisabled={props.isLoading || loadingRoles || loadingChurches}
                    />
                  </ChurchSelectWrapper>
                </>
              )}
            </div>
          );
        })}
      </InviteForm>
      <ModuleFooter>
        <Regular color={colors.error}>{formError}</Regular>
        <FooterButtons>
          <RhButton
            disabled={props.isLoading || loadingRoles || loadingChurches}
            mode={RhButtonMode.text}
            title="Cancel"
            onClick={props.onRequestClose}
          />
          <RhButton
            disabled={props.isLoading || loadingRoles || loadingChurches}
            title="OK"
            onClick={() => {
              setFormError(null);

              const emails = invitedUsers.map(user => user.email.toLowerCase());
              const uniqueEmails = new Set(emails);
              const hasDuplicate = emails.length !== uniqueEmails.size;
              if (hasDuplicate) {
                setFormError('Emails cannot be duplicated');
                return;
              }

              createValidationSchema(isSystemAdmin)
                .validate(invitedUsers)
                .then(() => {
                  props.onOK(invitedUsers);
                  setFormError(null);
                })
                .catch(error => setFormError(error.message));
            }}
          />
        </FooterButtons>
      </ModuleFooter>
    </RhModal>
  );
};

export const EmailInputs = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 13px;
  }
`;

export const InputDiv = styled.div`
  width: 100%;
`;

const ModuleFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 36px;
`;

const UserButtons = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 15px;
  }
`;

const InviteForm = styled.div`
  & > :not(:last-child) {
    padding-bottom: 22px;
    border-bottom: 1px solid ${colors.lightGrey};
    margin-bottom: 22px;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 24px;
  }
`;

const ChurchSelectWrapper = styled.div`
  width: 100%;
`;
